import { useState, useEffect, useCallback } from 'react';
import type { Tweaks, Accent } from '../types';

const ACCENT_PRESETS: Record<Accent, { blue: string; purple: string; purpleLight: string }> = {
  blue:   { blue: '#2D00F7', purple: '#6739FD', purpleLight: '#8900F2' },
  purple: { blue: '#6739FD', purple: '#8900F2', purpleLight: '#A855F7' },
  teal:   { blue: '#0D9488', purple: '#14B8A6', purpleLight: '#5EEAD4' },
  ink:    { blue: '#1A1F36', purple: '#3F4865', purpleLight: '#64748B' },
};

const DEFAULTS: Tweaks = {
  theme: 'modern',
  direction: 'A',
  density: 'regular',
  accent: 'blue',
  previewMode: false,
};

function applyTweaks(tweaks: Tweaks) {
  const root = document.documentElement;
  const body = document.body;

  body.setAttribute('data-direction', tweaks.direction);
  body.setAttribute('data-density', tweaks.density);

  if (tweaks.theme === '90s') {
    body.setAttribute('data-theme', '90s');
  } else {
    body.removeAttribute('data-theme');
  }

  const a = ACCENT_PRESETS[tweaks.accent];
  root.style.setProperty('--blue', a.blue);
  root.style.setProperty('--purple', a.purple);
  root.style.setProperty('--purple-light', a.purpleLight);
  root.style.setProperty('--gradient-brand', `linear-gradient(135deg, ${a.blue} 0%, ${a.purpleLight} 100%)`);
}

export function useTweaks() {
  const [tweaks, setTweaksState] = useState<Tweaks>(DEFAULTS);

  useEffect(() => {
    applyTweaks(tweaks);
  }, [tweaks]);

  const setTweak = useCallback(<K extends keyof Tweaks>(key: K, value: Tweaks[K]) => {
    setTweaksState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const toggleTheme = useCallback(() => {
    setTweaksState((prev) => ({ ...prev, theme: prev.theme === '90s' ? 'modern' : '90s' }));
  }, []);

  return { tweaks, setTweak, toggleTheme };
}
